import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartBar, FaFilePdf, FaLock, FaLockOpen } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Productionreport.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;
const SMS_WEBHOOK = 'https://sms.definiteassurance.com/webhook/d649cb06-3ad4-4298-ae27-e9b14f32d809';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const toMs = (input) => {
  if (!input) return 0;
  if (typeof input === 'string') return new Date(input).getTime();
  if (input instanceof Date) return input.getTime();
  return 0;
};

const formatKES = (amount) => {
  const n = parseFloat(amount);
  if (isNaN(n)) return '0.00';
  return n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const monthStartMs = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
};

const normalisePhone = (raw = '') => {
  const s = String(raw).replace(/\s+/g, '');
  if (s.startsWith('+254')) return s.substring(1);
  if (s.startsWith('254')) return s;
  if (s.startsWith('07')) return '254' + s.substring(1);
  if (s.startsWith('7')) return '254' + s;
  return s;
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Productionreport() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');
  const [phone, setPhone] = useState('');

  // ── Data ──
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // ── Date filter ──
  const [startMs, setStartMs] = useState(null);   // null = use current-month default
  const [endMs, setEndMs] = useState(null);
  const [monthName, setMonthname] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Commission unlock ──
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [modal, setModal] = useState(false);
  const [secretCode, setSecretCode] = useState(null);   // code from server
  const [enteredCode, setEnteredCode] = useState('');
  const [fetchingCode, setFetchingCode] = useState(false);
  const [resetting, setResetting] = useState(false);

  // ── PDF export ──
  const [exporting, setExporting] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(credentials ? { Authorization: `Basic ${credentials}` } : {}),
  }), [credentials]);

  // ══════════════════════════════════════
  //  CREDENTIALS
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, []);

  // ══════════════════════════════════════
  //  AGENT FROM LOCAL STORAGE
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) {
      setAgent(user);
    } else {
      navigate('/login');
    }
    // Set default month name
    setMonthname(new Date().toLocaleString('default', { month: 'long' }));
  }, [navigate]);

  // ══════════════════════════════════════
  //  AGENT CODE + PHONE
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers: authHeaders() });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
        setPhone(normalisePhone(data.Mobile));
      } catch {
        toast.error('Failed to load agent details.');
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FETCH PRODUCTION DATA
  // ══════════════════════════════════════
  const fetchProduction = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API}/getProduction?agent=${agentCode}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setProductions(Array.isArray(data) ? data : []);
    } catch {
      setFetchError('Failed to load production data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [agentCode, isAuth, authHeaders]);

  useEffect(() => { fetchProduction(); }, [fetchProduction]);

  // ══════════════════════════════════════
  //  DATE FILTER HANDLERS
  // ══════════════════════════════════════
  const handleStartDate = (e) => {
    const ms = new Date(e.target.value).getTime();
    setStartMs(ms);
    setMonthname(new Date(ms).toLocaleString('default', { month: 'long' }));
    setCurrentPage(1);
  };

  const handleEndDate = (e) => {
    setEndMs(new Date(e.target.value).getTime());
    setCurrentPage(1);
  };

  // ══════════════════════════════════════
  //  FILTERED DATA (client-side date filter)
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    const defaultStart = monthStartMs();
    return productions.filter(p => {
      const ms = toMs(p.DateDone);
      if (startMs !== null && endMs !== null) return ms >= startMs && ms <= endMs;
      if (startMs !== null) return ms >= startMs;
      return ms >= defaultStart;       // default: current month
    });
  }, [productions, startMs, endMs]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const currentRows = useMemo(
    () => filtered.slice(indexOfFirst, indexOfFirst + ROWS_PER_PAGE),
    [filtered, indexOfFirst]
  );
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ── Summary totals ──
  const totals = useMemo(() => ({
    grossPremium: filtered.reduce((s, p) => s + (p.GrossPremium || 0), 0),
    paidAmount: filtered.reduce((s, p) => s + (p.PaidAmount || 0), 0),
    netCommission: filtered.reduce((s, p) => s + (p.NetCommission || 0), 0),
    count: filtered.length,
  }), [filtered]);

  // Reset page when filter changes
  useEffect(() => { setCurrentPage(1); }, [startMs, endMs]);

  // ══════════════════════════════════════
  //  PDF EXPORT (jsPDF autotable)
  // ══════════════════════════════════════
  const exportPDF = useCallback(() => {
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');   // landscape for many columns
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text(`${monthName.toUpperCase()} PRODUCTION REPORT`, doc.internal.pageSize.getWidth() / 2, 14, { align: 'center' });

      const head = [['#', 'Date Done', 'Client', 'Product', 'Registration', 'Comm. Date', 'Expiry', 'Gross Premium', 'Premium Paid', ...(isUnlocked ? ['Net Commission'] : [])]];
      const body = filtered.map((p, i) => [
        i + 1,
        formatISODate(p.DateDone),
        p.Dealer_name ?? '--',
        p.ProductDescription ?? '--',
        p.Narration ?? '--',
        formatISODate(p.CommencementDate),
        formatISODate(p.ExpiryDate),
        formatKES(p.GrossPremium),
        formatKES(p.PaidAmount),
        ...(isUnlocked ? [formatKES(p.NetCommission)] : []),
      ]);

      doc.autoTable({
        head,
        body,
        startY: 20,
        styles: { fontSize: 7.5, cellPadding: 2 },
        headStyles: { fillColor: [26, 92, 90], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 250, 249] },
        foot: [[
          '', '', '', '', '', '', 'TOTALS',
          formatKES(totals.grossPremium),
          formatKES(totals.paidAmount),
          ...(isUnlocked ? [formatKES(totals.netCommission)] : []),
        ]],
        footStyles: { fillColor: [240, 247, 246], textColor: [26, 92, 90], fontStyle: 'bold' },
      });

      doc.save(`production_report_${monthName.toLowerCase()}.pdf`);
    } catch {
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [filtered, isUnlocked, monthName, totals]);

  // ══════════════════════════════════════
  //  VIEW COMMISSIONS (fetch & send code)
  // ══════════════════════════════════════
  const openCommissionModal = async () => {
    if (isUnlocked) {
      // Already unlocked — just lock again
      setIsUnlocked(false);
      return;
    }
    setFetchingCode(true);
    try {
      const res = await fetch(`${API}/getGeneratedcode?agent=${agentCode}`, { headers: authHeaders() });
      const data = await res.json();
      setSecretCode(data.code);
      if (data.new && phone) {
        await axios.post(SMS_WEBHOOK, {
          MessageParameters: [{ Number: [phone], Text: `${data.code}` }],
        });
        toast.success('Verification code sent to your phone.');
      }
    } catch {
      toast.error('Failed to fetch verification code.');
    } finally {
      setFetchingCode(false);
      setEnteredCode('');
      setModal(true);
    }
  };

  // ══════════════════════════════════════
  //  RESET CODE
  // ══════════════════════════════════════
  const resetCode = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API}/Resetcode?agent=${agentCode}`, { headers: authHeaders() });
      const data = await res.json();
      setSecretCode(data.code);
      if (data.new && phone) {
        await axios.post(SMS_WEBHOOK, {
          MessageParameters: [{ Number: [phone], Text: `${data.code}` }],
        });
        toast.success('New code sent to your phone.');
      }
    } catch {
      toast.error('Failed to reset code.');
    } finally {
      setResetting(false);
    }
  };

  // ══════════════════════════════════════
  //  VERIFY CODE
  // ══════════════════════════════════════
  const verifyCode = () => {
    if (String(enteredCode).trim() === String(secretCode).trim()) {
      setIsUnlocked(true);
      setModal(false);
      toast.success('Commissions unlocked!');
    } else {
      toast.error('Incorrect code. Please try again.');
    }
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="prod-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="prod-main">
        {/* Breadcrumb */}
        <div className="prod-top-row">
          <span className="prod-company">Definite Assurance</span>
          <span className="prod-breadcrumb">Dashboard &rsaquo; Production Report</span>
        </div>

        <div className="prod-card">
          <div className="prod-card-header">
            <h3><FaChartBar /> Production Report — {monthName}</h3>
          </div>

          {/* Toolbar */}
          <div className="prod-toolbar">
            <div className="prod-date-group">
              <label>From Date</label>
              <input type="date" onChange={handleStartDate} />
            </div>
            <div className="prod-date-group">
              <label>To Date</label>
              <input type="date" onChange={handleEndDate} />
            </div>
            <div className="prod-toolbar-actions">
              <button
                className="prod-export-btn"
                onClick={exportPDF}
                disabled={exporting || filtered.length === 0}
              >
                <FaFilePdf /> {exporting ? 'Exporting…' : 'Export PDF'}
              </button>
              <button
                className="prod-commission-btn"
                onClick={openCommissionModal}
                disabled={fetchingCode}
              >
                {isUnlocked ? <><FaLockOpen /> Hide Commissions</> : <><FaLock /> {fetchingCode ? 'Please wait…' : 'View Commissions'}</>}
              </button>
            </div>
          </div>

          {/* Summary strip */}
          {!loading && filtered.length > 0 && (
            <div className="prod-summary">
              <div className="prod-summary-item">
                <span>Records</span>
                <span>{totals.count}</span>
              </div>
              <div className="prod-summary-item">
                <span>Total Gross Premium</span>
                <span>KES {formatKES(totals.grossPremium)}</span>
              </div>
              <div className="prod-summary-item">
                <span>Total Premium Paid</span>
                <span>KES {formatKES(totals.paidAmount)}</span>
              </div>
              {isUnlocked && (
                <div className="prod-summary-item">
                  <span>Net Commission</span>
                  <span>KES {formatKES(totals.netCommission)}</span>
                </div>
              )}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="prod-loading">
              <div className="prod-spinner" />
              <p>Loading production data…</p>
            </div>
          ) : fetchError ? (
            <div className="prod-error-state">
              <p>{fetchError}</p>
              <button onClick={fetchProduction}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="prod-empty">
              No production records found for the selected period.
            </div>
          ) : (
            <>
              <div className="prod-table-wrap">
                <table className="prod-table" id="prod-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date Done</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Registration</th>
                      <th>Comm. Date</th>
                      <th>Expiry Date</th>
                      <th>Gross Premium</th>
                      <th>Premium Paid</th>
                      <th>Net Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, idx) => (
                      <tr key={val.OID ?? idx}>
                        <td>{indexOfFirst + idx + 1}</td>
                        <td>{formatISODate(val.DateDone)}</td>
                        <td>{val.Dealer_name ?? '--'}</td>
                        <td>{val.ProductDescription ?? '--'}</td>
                        <td>{val.Narration ?? '--'}</td>
                        <td>{formatISODate(val.CommencementDate)}</td>
                        <td>{formatISODate(val.ExpiryDate)}</td>
                        <td>{formatKES(val.GrossPremium)}</td>
                        <td>{formatKES(val.PaidAmount)}</td>
                        <td>
                          {isUnlocked
                            ? formatKES(val.NetCommission)
                            : <span className="prod-commission-hidden">••••••</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'right' }}>Totals</td>
                      <td>KES {formatKES(totals.grossPremium)}</td>
                      <td>KES {formatKES(totals.paidAmount)}</td>
                      <td>{isUnlocked ? `KES ${formatKES(totals.netCommission)}` : '——'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              <div className="prod-pagination">
                <span className="prod-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfFirst + ROWS_PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="prod-pagination-controls">
                  <button
                    className="prod-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="prod-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`prod-page-btn${currentPage === p ? ' active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                  )}
                  <button
                    className="prod-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Commission Verify Modal ── */}
      {modal && (
        <div className="prod-modal-overlay">
          <div className="prod-modal">
            <div className="prod-modal-header">
              <h4><FaLock style={{ marginRight: 6 }} />View Commissions</h4>
              <button className="prod-modal-close" onClick={() => setModal(false)} aria-label="Close">&times;</button>
            </div>
            <p>Enter the secret code sent to your registered phone number to unlock commission figures.</p>

            <div className="prod-form-group">
              <label>Secret Code</label>
              <input
                type="text"
                placeholder="Enter code…"
                value={enteredCode}
                onChange={e => setEnteredCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verifyCode()}
                autoFocus
              />
            </div>

            <button className="prod-verify-btn" onClick={verifyCode}>
              Verify &amp; Unlock
            </button>

            <button
              className="prod-reset-link"
              onClick={resetCode}
              disabled={resetting}
            >
              {resetting ? 'Resending…' : 'Reset / resend code'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
